import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CalculatorIcon, TrendingUpIcon, AwardIcon, AlertCircleIcon, CheckCircleIcon, StarIcon } from 'lucide-react';
interface PerformanceZone {
  zone: string;
  emoji: string;
  percentage: number;
  color: string;
  bgGradient: string;
  minThreshold: number;
  level: string;
  description: string;
}
const zones: PerformanceZone[] = [{
  zone: 'Zona Roșie',
  emoji: '🔴',
  percentage: 30,
  color: 'text-red-700',
  bgGradient: 'from-red-100 to-red-200',
  minThreshold: 0,
  level: 'Minim',
  description: 'Performanță sub target, risc de audit.'
}, {
  zone: 'Zona Portocalie',
  emoji: '🟠',
  percentage: 40,
  color: 'text-orange-700',
  bgGradient: 'from-orange-100 to-orange-200',
  minThreshold: 2450,
  level: 'În creștere',
  description: 'Nivel de recuperare – analiză și recomandări.'
}, {
  zone: 'Zona Verde 1',
  emoji: '🟢',
  percentage: 45,
  color: 'text-green-700',
  bgGradient: 'from-green-100 to-green-200',
  minThreshold: 3850,
  level: 'Stabilă',
  description: 'Performanță corectă, în linie cu standardele agenției.'
}, {
  zone: 'Zona Verde 2',
  emoji: '🟢',
  percentage: 50,
  color: 'text-green-800',
  bgGradient: 'from-green-200 to-green-300',
  minThreshold: 4900,
  level: 'Ridicată',
  description: 'Activitate constantă, eligibil pentru bonusuri suplimentare.'
}, {
  zone: 'Zona Verde 3',
  emoji: '🟢',
  percentage: 55,
  color: 'text-green-900',
  bgGradient: 'from-green-300 to-green-400',
  minThreshold: 7250,
  level: 'Excelentă',
  description: 'Top Performers – bonusuri extra și recunoaștere publică.'
}];
function getZoneForAmount(amount: number): PerformanceZone {
  if (amount >= 7250) return zones[4];
  if (amount >= 4900) return zones[3];
  if (amount >= 3850) return zones[2];
  if (amount >= 2450) return zones[1];
  return zones[0];
}
const quickAmounts = [2000, 3500, 5000, 8000];
export function AgencyCommissionCalculator() {
  const [transactionCount, setTransactionCount] = useState<string>('');
  const [totalCommissions, setTotalCommissions] = useState<string>('');
  const [showResult, setShowResult] = useState(false);
  const handleCalculate = () => {
    if (parseFloat(totalCommissions) > 0) {
      setShowResult(true);
    }
  };
  const commissionsNum = parseFloat(totalCommissions) || 0;
  const transactionsNum = parseInt(transactionCount) || 0;
  const zone = getZoneForAmount(commissionsNum);
  const agentCommission = commissionsNum * (zone.percentage / 100);
  return <div className="space-y-8" data-id="element-113">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-114">
        {/* Calculator Input */}
        <div className="space-y-6" data-id="element-115">
          <div className="bg-white rounded-2xl p-8 shadow-xl" data-id="element-116">
            <div className="flex items-center space-x-3 mb-6" data-id="element-117">
              <CalculatorIcon className="w-8 h-8 text-[#357BEB]" data-id="element-118" />
              <h2 className="text-2xl font-bold text-[#132C53]" data-id="element-119">
                Calculează comisionul tău
              </h2>
            </div>
            <div className="space-y-6" data-id="element-120">
              <div data-id="element-121">
                <label className="block text-sm font-semibold mb-3 text-[#132C53]" data-id="element-122">
                  Număr total tranzacții realizate
                </label>
                <input type="number" value={transactionCount} onChange={e => setTransactionCount(e.target.value)} className="w-full text-2xl font-bold text-center text-[#132C53] bg-gray-50 border-2 border-gray-200 rounded-xl p-4 focus:outline-none focus:border-[#357BEB] transition-colors" placeholder="5" min="0" data-id="element-123" />
              </div>
              <div data-id="element-124">
                <label className="block text-sm font-semibold mb-3 text-[#132C53]" data-id="element-125">
                  Total comisioane agenție cumulate (€)
                </label>
                <input type="number" value={totalCommissions} onChange={e => setTotalCommissions(e.target.value)} className="w-full text-3xl font-bold text-center text-[#132C53] bg-gray-50 border-2 border-gray-200 rounded-xl p-4 focus:outline-none focus:border-[#357BEB] transition-colors" placeholder="3500" min="0" step="100" data-id="element-126" />
              </div>
              <div className="grid grid-cols-4 gap-3" data-id="element-127">
                {quickAmounts.map(amount => <button key={amount} onClick={() => setTotalCommissions(amount.toString())} className={`p-3 rounded-xl border-2 font-bold transition-all ${totalCommissions === amount.toString() ? 'border-[#357BEB] bg-[#357BEB] text-white' : 'border-gray-200 bg-white text-[#132C53]'}`} data-id="element-128">
                    {amount.toLocaleString()}€
                  </button>)}
              </div>
              <button onClick={handleCalculate} disabled={!totalCommissions || parseFloat(totalCommissions) <= 0} className="w-full bg-gradient-to-r from-[#357BEB] to-[#21A366] text-white px-6 py-4 rounded-2xl font-bold hover:shadow-xl transition-shadow disabled:opacity-50" data-id="element-129">
                Calculează zona și comisionul
              </button>
            </div>
          </div>
        </div>
        {/* Results */}
        <div className="space-y-6" data-id="element-130">
          {showResult && commissionsNum > 0 ? <div className="bg-white rounded-2xl p-8 shadow-xl" data-id="element-131">
              <h2 className="text-2xl font-bold text-[#132C53] mb-6" data-id="element-132">
                Rezultate lunare
              </h2>
              <div className="space-y-6" data-id="element-133">
                {transactionsNum > 0 && <div className="bg-gray-50 rounded-xl p-6" data-id="element-134">
                    <p className="text-sm font-medium text-gray-600 mb-2" data-id="element-135">
                      Număr tranzacții realizate
                    </p>
                    <p className="text-4xl font-bold text-gray-700" data-id="element-136">
                      {transactionsNum}
                    </p>
                  </div>}
                <div className="bg-blue-50 rounded-xl p-6" data-id="element-137">
                  <p className="text-sm font-medium text-gray-600 mb-2" data-id="element-138">
                    Total comisioane agenție
                  </p>
                  <p className="text-4xl font-bold text-[#357BEB]" data-id="element-139">
                    {commissionsNum.toLocaleString('ro-RO', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                    €
                  </p>
                </div>
                <div className={`bg-gradient-to-r ${zone.bgGradient} rounded-xl p-6 border-2 border-gray-200`} data-id="element-140">
                  <div className="flex items-center space-x-3 mb-3" data-id="element-141">
                    <span className="text-4xl" data-id="element-142">{zone.emoji}</span>
                    <div data-id="element-143">
                      <h3 className={`text-xl font-bold ${zone.color}`} data-id="element-144">
                        {zone.zone}
                      </h3>
                      <span className={`text-2xl font-bold ${zone.color}`} data-id="element-145">
                        {zone.percentage}%
                      </span>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border-2 border-green-200" data-id="element-146">
                  <p className="text-sm font-medium text-gray-600 mb-2" data-id="element-147">
                    Comisionul tău lunar
                  </p>
                  <p className="text-4xl font-bold text-green-700" data-id="element-148">
                    {agentCommission.toLocaleString('ro-RO', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                    €
                  </p>
                </div>
              </div>
            </div> : <div className="bg-white rounded-2xl p-12 shadow-xl text-center" data-id="element-149">
              <div className="text-6xl mb-4" data-id="element-150">📊</div>
              <h3 className="text-xl font-bold text-[#132C53] mb-2" data-id="element-151">
                Completează datele pentru calcul
              </h3>
            </div>}
        </div>
      </div>
      {/* Performance Scale */}
      <div className="mt-12" data-id="element-152">
        <div className="text-center mb-8" data-id="element-153">
          <h2 className="text-3xl font-bold text-[#132C53] mb-4" data-id="element-154">
            📊 Scara Performanței
          </h2>
        </div>
        <div className="space-y-4" data-id="element-155">
          {zones.map((zoneItem, index) => {
          const isCurrentZone = zoneItem.zone === zone.zone && showResult;
          return <div key={zoneItem.zone} className={`bg-gradient-to-r ${zoneItem.bgGradient} border-2 ${isCurrentZone ? 'border-[#357BEB] shadow-2xl' : 'border-gray-300'} rounded-2xl p-6`} data-id="element-156">
                <div className="flex items-start space-x-4" data-id="element-157">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-3xl" data-id="element-158">
                    {zoneItem.emoji}
                  </div>
                  <div className="flex-1" data-id="element-159">
                    <h4 className={`text-xl font-bold ${zoneItem.color} mb-2`} data-id="element-160">
                      {zoneItem.zone}
                      {isCurrentZone && <span className="ml-3 text-sm bg-[#357BEB] text-white px-3 py-1 rounded-full" data-id="element-161">
                          Zona ta actuală
                        </span>}
                    </h4>
                    <div className="grid grid-cols-3 gap-4 mb-3" data-id="element-162">
                      <div data-id="element-163">
                        <p className="text-xs text-gray-600" data-id="element-164">Nivel</p>
                        <p className={`font-bold ${zoneItem.color}`} data-id="element-165">
                          {zoneItem.level}
                        </p>
                      </div>
                      <div data-id="element-166">
                        <p className="text-xs text-gray-600" data-id="element-167">Prag minim</p>
                        <p className={`font-bold ${zoneItem.color}`} data-id="element-168">
                          {zoneItem.minThreshold.toLocaleString()}€
                        </p>
                      </div>
                      <div data-id="element-169">
                        <p className="text-xs text-gray-600" data-id="element-170">Comision</p>
                        <p className={`text-lg font-bold ${zoneItem.color}`} data-id="element-171">
                          {zoneItem.percentage}%
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700" data-id="element-172">
                      {zoneItem.description}
                    </p>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </div>;
}